import { useState, useEffect, useRef, useCallback } from 'react';
import { FinalizedSentence } from '../types';

// Declare Web Speech API types
declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

interface UseContinuousSpeechRecognitionOptions {
  onInterimResult?: (text: string) => void;
  onSentenceFinalized?: (sentence: FinalizedSentence) => void;
  onTopicComplete?: (sentences: FinalizedSentence[]) => void;
  onError?: (error: string) => void;
  sentencePauseMs?: number;  // Default: 1500
  topicPauseMs?: number;     // Default: 4500
  autoStart?: boolean;       // Default: false
  language?: string;         // Default: 'en-US'
  disabled?: boolean;        // External control for muting
}

export function useContinuousSpeechRecognition(
  options: UseContinuousSpeechRecognitionOptions = {}
) {
  const {
    onInterimResult,
    onSentenceFinalized,
    onTopicComplete,
    onError,
    sentencePauseMs = 1500,
    topicPauseMs = 4500,
    autoStart = false,
    language = 'en-US',
    disabled = false
  } = options;

  // State
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [finalizedSentences, setFinalizedSentences] = useState<FinalizedSentence[]>([]);
  const [pauseDuration, setPauseDuration] = useState(0);

  // Refs
  const recognitionRef = useRef<any>(null);
  const sentenceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const topicTimerRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pauseStartTimeRef = useRef<number | null>(null);
  const pauseDurationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const shouldKeepListeningRef = useRef(false);
  const isRestartingRef = useRef(false);
  const lastResultTimeRef = useRef<number>(Date.now());
  const accumulatedInterimRef = useRef<string>('');
  // Mirror of finalizedSentences state — updated synchronously so timers can read it
  // without relying on React state-updater side effects (which run lazily in React 18).
  const finalizedSentencesRef = useRef<FinalizedSentence[]>([]);
  const interimFallbackTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Update callback refs when they change
  const onInterimResultRef = useRef(onInterimResult);
  const onSentenceFinalizedRef = useRef(onSentenceFinalized);
  const onTopicCompleteRef = useRef(onTopicComplete);
  const onErrorRef = useRef(onError);
  const finalizeSentenceRef = useRef<((text: string, confidence: number) => void) | null>(null);

  useEffect(() => {
    onInterimResultRef.current = onInterimResult;
    onSentenceFinalizedRef.current = onSentenceFinalized;
    onTopicCompleteRef.current = onTopicComplete;
    onErrorRef.current = onError;
  }, [onInterimResult, onSentenceFinalized, onTopicComplete, onError]);

  // Clear all timers helper
  const clearAllTimers = useCallback(() => {
    if (sentenceTimerRef.current) {
      clearTimeout(sentenceTimerRef.current);
      sentenceTimerRef.current = null;
    }
    if (topicTimerRef.current) {
      clearTimeout(topicTimerRef.current);
      topicTimerRef.current = null;
    }
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
    if (pauseDurationIntervalRef.current) {
      clearInterval(pauseDurationIntervalRef.current);
      pauseDurationIntervalRef.current = null;
    }
    if (interimFallbackTimerRef.current) {
      clearTimeout(interimFallbackTimerRef.current);
      interimFallbackTimerRef.current = null;
    }
    pauseStartTimeRef.current = null;
    setPauseDuration(0);
  }, []);

  // Start pause duration tracking
  const startPauseDurationTracking = useCallback(() => {
    pauseStartTimeRef.current = Date.now();

    // Update pause duration every 100ms for UI
    pauseDurationIntervalRef.current = setInterval(() => {
      if (pauseStartTimeRef.current) {
        const duration = Date.now() - pauseStartTimeRef.current;
        setPauseDuration(duration);
      }
    }, 100);
  }, []);

  // Stop pause duration tracking
  const stopPauseDurationTracking = useCallback(() => {
    if (pauseDurationIntervalRef.current) {
      clearInterval(pauseDurationIntervalRef.current);
      pauseDurationIntervalRef.current = null;
    }
    pauseStartTimeRef.current = null;
    setPauseDuration(0);
  }, []);

  // Finalize sentence and add to queue
  const finalizeSentence = useCallback((text: string, confidence: number) => {
    if (!text.trim()) return;

    const sentence: FinalizedSentence = {
      id: `sentence-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      text: text.trim(),
      confidence,
      timestamp: new Date().toISOString(),
      isNew: true
    };

    // Keep the ref in sync — this is the authoritative source for timers/flush paths.
    const newQueue = [...finalizedSentencesRef.current, sentence];
    finalizedSentencesRef.current = newQueue;

    // Update state for UI rendering.
    setFinalizedSentences(newQueue);

    // Auto-flush if queue gets too large (10 sentences) — done outside a state updater
    // to avoid the React strict-mode double-invocation problem.
    if (newQueue.length >= 10) {
      const captured = [...newQueue];
      finalizedSentencesRef.current = [];
      setFinalizedSentences([]);
      setTimeout(() => {
        if (onTopicCompleteRef.current) {
          onTopicCompleteRef.current(captured);
        }
      }, 100);
    }

    // Mark as not new after 3 seconds for fade animation
    setTimeout(() => {
      setFinalizedSentences(prev =>
        prev.map(s => s.id === sentence.id ? { ...s, isNew: false } : s)
      );
    }, 3000);

    if (onSentenceFinalizedRef.current) {
      onSentenceFinalizedRef.current(sentence);
    }

    // Start topic pause timer (but clear any existing one first)
    if (topicTimerRef.current) {
      clearTimeout(topicTimerRef.current);
      topicTimerRef.current = null;
    }
    stopPauseDurationTracking();

    startPauseDurationTracking();
    topicTimerRef.current = setTimeout(() => {
      // Read synchronously from the ref — avoids the React 18 state-updater side-effect
      // race where the updater runs lazily and captured is empty when setTimeout(0) fires.
      const captured = [...finalizedSentencesRef.current];
      finalizedSentencesRef.current = [];
      setFinalizedSentences([]);

      if (captured.length > 0 && onTopicCompleteRef.current) {
        onTopicCompleteRef.current(captured);
      }
      stopPauseDurationTracking();
    }, topicPauseMs);
  }, [topicPauseMs, startPauseDurationTracking, stopPauseDurationTracking]);

  // Update finalizeSentence ref whenever it changes
  useEffect(() => {
    finalizeSentenceRef.current = finalizeSentence;
  }, [finalizeSentence]);

  // Clear sentence timer
  const clearSentenceTimer = useCallback(() => {
    if (sentenceTimerRef.current) {
      clearTimeout(sentenceTimerRef.current);
      sentenceTimerRef.current = null;
    }
  }, []);

  // Clear topic timer
  const clearTopicTimer = useCallback(() => {
    if (topicTimerRef.current) {
      clearTimeout(topicTimerRef.current);
      topicTimerRef.current = null;
    }
    stopPauseDurationTracking();
  }, [stopPauseDurationTracking]);

  // Initialize speech recognition ONCE on mount
  useEffect(() => {
    const initRecognition = async () => {
      try {
        // Check browser support
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

        if (!SpeechRecognition) {
          console.warn('[Continuous STT] Speech recognition not supported in this browser');
          setIsSupported(false);
          return;
        }

        setIsSupported(true);

        // Request microphone permission
        try {
          await navigator.mediaDevices.getUserMedia({ audio: true });
          setPermissionGranted(true);
        } catch (permError) {
          console.warn('[Continuous STT] Microphone permission not granted:', permError);
          setPermissionGranted(false);
        }

        // Create recognition instance
        const recognition = new SpeechRecognition();
        recognition.continuous = true;        // Continuous listening
        recognition.interimResults = true;    // Show live transcription
        recognition.lang = language;
        recognition.maxAlternatives = 1;

        recognition.onstart = () => {
          setIsListening(true);
          isRestartingRef.current = false;
          lastResultTimeRef.current = Date.now();
        };

        recognition.onresult = (event: any) => {
          const currentResultIndex = event.resultIndex;
          const result = event.results[currentResultIndex];
          const transcript = result[0].transcript;
          const confidence = result[0].confidence;
          const isFinal = result.isFinal;

          lastResultTimeRef.current = Date.now();

          if (!isFinal) {
            // Interim result - show live
            setInterimTranscript(transcript);
            accumulatedInterimRef.current = transcript;

            if (onInterimResultRef.current) {
              onInterimResultRef.current(transcript);
            }

            // Reset timers (user is still speaking)
            clearSentenceTimer();
            clearTopicTimer();

            // Fallback: if browser never sends isFinal, treat interim as final after silence
            if (interimFallbackTimerRef.current) {
              clearTimeout(interimFallbackTimerRef.current);
            }
            interimFallbackTimerRef.current = setTimeout(() => {
              const accumulated = accumulatedInterimRef.current;
              if (accumulated && accumulated.trim().length >= 3) {
                setInterimTranscript('');
                accumulatedInterimRef.current = '';
                if (finalizeSentenceRef.current) {
                  finalizeSentenceRef.current(accumulated, 0.8);
                }
              }
            }, sentencePauseMs + 1000);
          } else {
            // Final result - finalize sentence

            // Clear interim fallback timer since we got a proper final
            if (interimFallbackTimerRef.current) {
              clearTimeout(interimFallbackTimerRef.current);
              interimFallbackTimerRef.current = null;
            }

            // Clear interim
            setInterimTranscript('');
            accumulatedInterimRef.current = '';

            // Don't finalize if sentence is too short or low confidence
            if (transcript.trim().length < 3 || confidence < 0.3) {
              return;
            }

            // Start sentence finalization timer (allows for mid-sentence pauses)
            clearSentenceTimer();
            sentenceTimerRef.current = setTimeout(() => {
              if (finalizeSentenceRef.current) {
                finalizeSentenceRef.current(transcript, confidence);
              }
            }, sentencePauseMs);
          }
        };

        recognition.onerror = (event: any) => {
          console.error('[Continuous STT] Speech recognition error:', event.error);

          let errorMsg: string;
          switch (event.error) {
            case 'no-speech':
              // Chrome's ~10s silence timeout. Preemptively restart so the mic
              // doesn't appear to drop out — onend may also fire but belt-and-braces.
              if (shouldKeepListeningRef.current && !disabled) {
                isRestartingRef.current = true;
                try {
                  recognitionRef.current?.stop();
                } catch (e) {
                  // ignore — onend will still drive the restart path
                }
              }
              return;
            case 'audio-capture':
              errorMsg = 'No microphone found. Please check your device.';
              break;
            case 'not-allowed':
              errorMsg = 'Microphone permission denied. Please enable it in your browser settings.';
              setPermissionGranted(false);
              shouldKeepListeningRef.current = false;
              setIsListening(false);
              break;
            case 'network':
              errorMsg = 'Network error. Please check your connection.';
              break;
            default:
              errorMsg = `Speech recognition error: ${event.error}`;
          }

          if (onErrorRef.current) {
            onErrorRef.current(errorMsg);
          }
        };

        recognition.onend = () => {
          // Only flip UI to "off" if we genuinely intend to stop. During an
          // auto-restart cycle (e.g. Chrome no-speech timeout), keep the mic
          // indicator solid so users don't see it flicker every ~10s.
          if (!shouldKeepListeningRef.current || disabled) {
            setIsListening(false);
            isRestartingRef.current = false;
            return;
          }

          isRestartingRef.current = true;

          const attemptRestart = (retry: boolean) => {
            try {
              recognitionRef.current?.start();
            } catch (e) {
              console.error('[Continuous STT] Auto-restart failed:', e);
              if (retry) {
                setTimeout(() => attemptRestart(false), 500);
              } else {
                setIsListening(false);
                isRestartingRef.current = false;
              }
            }
          };

          setTimeout(() => attemptRestart(true), 300);
        };

        recognitionRef.current = recognition;

      } catch (error) {
        console.error('[Continuous STT] Failed to initialize speech recognition:', error);
        setIsSupported(false);
      }
    };

    initRecognition();

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (e) {
          console.warn('[Continuous STT] Error during cleanup abort:', e);
        }
      }
      if (sentenceTimerRef.current) clearTimeout(sentenceTimerRef.current);
      if (topicTimerRef.current) clearTimeout(topicTimerRef.current);
      if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
      if (pauseDurationIntervalRef.current) clearInterval(pauseDurationIntervalRef.current);
      if (interimFallbackTimerRef.current) clearTimeout(interimFallbackTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Handle disabled prop changes
  useEffect(() => {
    if (disabled && isListening) {
      // Call stop directly - it's defined below and uses refs internally
      shouldKeepListeningRef.current = false;
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          console.warn('[Continuous STT] Error stopping recognition:', e);
        }
      }
      clearAllTimers();
      setInterimTranscript('');
      accumulatedInterimRef.current = '';
    }
  }, [disabled, isListening, clearAllTimers]);

  // Heartbeat to prevent Chrome timeout (restart every 45s)
  useEffect(() => {
    if (!isListening) return;

    heartbeatTimerRef.current = setInterval(() => {
      if (recognitionRef.current && isListening) {
        try {
          recognitionRef.current.stop(); // Will trigger onend → auto-restart
        } catch (e) {
          console.error('[Continuous STT] Heartbeat restart failed:', e);
        }
      }
    }, 45000); // 45 seconds

    return () => {
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
    };
  }, [isListening]);

  // Start continuous listening
  const start = useCallback(() => {
    if (!recognitionRef.current) {
      if (onErrorRef.current) {
        onErrorRef.current('Speech recognition not available in this browser. Please use Chrome, Edge, or Safari 14.1+.');
      }
      return;
    }

    if (disabled) {
      return;
    }

    if (!permissionGranted) {
      if (onErrorRef.current) {
        onErrorRef.current('Microphone permission denied. Please enable it in your browser settings.');
      }
      return;
    }

    // Check if already running via ref instead of state
    if (shouldKeepListeningRef.current) {
      return;
    }

    shouldKeepListeningRef.current = true;

    try {
      recognitionRef.current.start();
    } catch (error: any) {
      console.error('[Continuous STT] Failed to start recognition:', error);

      // If recognition is already running, stop and restart
      if (error.message && error.message.includes('already started')) {
        recognitionRef.current.stop();
        setTimeout(() => {
          try {
            recognitionRef.current?.start();
          } catch (e) {
            console.error('[Continuous STT] Retry failed:', e);
            if (onErrorRef.current) {
              onErrorRef.current('Failed to start microphone');
            }
          }
        }, 100);
      } else {
        if (onErrorRef.current) {
          onErrorRef.current('Failed to start microphone: ' + error.message);
        }
      }
    }
  }, [disabled]);

  // Stop continuous listening
  const stop = useCallback(() => {
    shouldKeepListeningRef.current = false;

    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        console.warn('[Continuous STT] Error stopping recognition:', e);
      }
    }

    // Clear all timers first
    clearAllTimers();

    // Flush queued sentences + any accumulated interim text — read from the ref
    // synchronously so we don't rely on a React state-updater side effect.
    const accumulated = accumulatedInterimRef.current;
    const toSend: FinalizedSentence[] = [...finalizedSentencesRef.current];
    finalizedSentencesRef.current = [];
    setFinalizedSentences([]);

    // Also include any accumulated interim text as a sentence
    if (accumulated && accumulated.trim().length >= 3) {
      toSend.push({
        id: `sentence-${Date.now()}-stop`,
        text: accumulated.trim(),
        confidence: 0.8,
        timestamp: new Date().toISOString(),
        isNew: true
      });
    }

    if (toSend.length > 0 && onTopicCompleteRef.current) {
      onTopicCompleteRef.current(toSend);
    }

    // Clear interim transcript
    setInterimTranscript('');
    accumulatedInterimRef.current = '';
  }, [clearAllTimers]);

  // Clear queue
  const clearQueue = useCallback(() => {
    finalizedSentencesRef.current = [];
    setFinalizedSentences([]);
    clearTopicTimer();
  }, [clearTopicTimer]);

  // Send queue immediately (manual trigger)
  const sendQueueNow = useCallback(() => {
    const captured = [...finalizedSentencesRef.current];
    finalizedSentencesRef.current = [];
    setFinalizedSentences([]);
    clearTopicTimer();
    if (captured.length > 0 && onTopicCompleteRef.current) {
      onTopicCompleteRef.current(captured);
    }
  }, [clearTopicTimer]);

  // Auto-start if requested
  useEffect(() => {
    if (autoStart && isSupported && permissionGranted && !isListening) {
      start();
    }
  }, [autoStart, isSupported, permissionGranted, isListening, start]);

  return {
    // State
    isListening,
    isSupported,
    permissionGranted,
    interimTranscript,
    finalizedSentences,
    pauseDuration,

    // Methods
    start,
    stop,
    clearQueue,
    sendQueueNow
  };
}
